import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import bbhLogoUrl from '@/assets/bbh-logo.svg';

interface SigningField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'textarea';
  required: boolean;
}

interface SigningData {
  template_key: string;
  title: string;
  body: string;
  fields: SigningField[];
  prefill: Record<string, string>;
}

type LoadState = 'loading' | 'invalid' | 'ready' | 'submitted';

const COLORS = {
  bg: '#f8fafc',
  card: '#fff',
  border: '#e2e8f0',
  inputBorder: '#cbd5e1',
  text: '#0f172a',
  subtext: '#475569',
  accent: '#0f766e',
  accentDisabled: '#94a3b8',
  error: '#b91c1c',
};

function renderTypedSignatureToPng(text: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 480;
  canvas.height = 140;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#0f172a';
  ctx.font = "48px 'Brush Script MT', cursive";
  ctx.textBaseline = 'middle';
  ctx.fillText(text || ' ', 16, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

const Sign: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<SigningData | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [signatureMode, setSignatureMode] = useState<'typed' | 'drawn'>('typed');
  const [typedSignature, setTypedSignature] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch(`/api/sign/${token}`);
        const json: any = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !json?.success) {
          setState('invalid');
          return;
        }

        const signingData = json.data as SigningData;
        setData(signingData);
        setValues({ ...signingData.prefill });
        setTypedSignature(signingData.prefill.signer_name || '');
        setState('ready');
      } catch {
        if (!cancelled) setState('invalid');
      }
    }

    if (token) load();
    else setState('invalid');

    return () => {
      cancelled = true;
    };
  }, [token]);

  function updateValue(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function getCanvasPoint(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function startDraw(clientX: number, clientY: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawingRef.current = true;
    const { x, y } = getCanvasPoint(canvas, clientX, clientY);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function drawTo(clientX: number, clientY: number) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(canvas, clientX, clientY);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  }

  function endDraw() {
    drawingRef.current = false;
  }

  function clearCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }

  useEffect(() => {
    if (signatureMode === 'drawn' && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [signatureMode]);

  const hasSignature = signatureMode === 'typed' ? typedSignature.trim().length > 0 : hasDrawn;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!data || !token) return;
    setError(null);

    for (const field of data.fields) {
      if (field.required && !values[field.key]?.trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }
    if (!hasSignature) {
      setError('A signature is required');
      return;
    }

    const signatureImage =
      signatureMode === 'typed'
        ? renderTypedSignatureToPng(typedSignature.trim())
        : canvasRef.current?.toDataURL('image/png') || '';

    setSubmitting(true);
    try {
      const res = await fetch(`/api/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signer_name: values.signer_name || '',
          signer_email: values.signer_email || '',
          signer_role: values.signer_role || '',
          signer_address: values.signer_address || '',
          signer_comments: values.signer_comments || '',
          signature_type: signatureMode,
          signature_image: signatureImage,
        }),
      });
      const json: any = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        if (res.status === 404 || res.status === 410) {
          setState('invalid');
          return;
        }
        throw new Error(json?.message || `HTTP ${res.status}`);
      }

      setState('submitted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit signed document.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: COLORS.bg,
        padding: '1.5rem 1rem',
      }}
    >
      <a
        href="https://backedbyhumans.com/"
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', marginBottom: '1.25rem' }}
      >
        <img src={bbhLogoUrl} alt="Backed by Humans" style={{ display: 'block', height: 80, width: 'auto' }} />
      </a>
      <section
        style={{
          width: '100%',
          maxWidth: 680,
          background: COLORS.card,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: '1.5rem',
        }}
      >
        {state === 'loading' && <p style={{ color: COLORS.subtext }}>Loading…</p>}

        {state === 'invalid' && (
          <>
            <h1 style={{ margin: 0, fontSize: '1.25rem', color: COLORS.text }}>Link no longer valid</h1>
            <p style={{ marginTop: '0.75rem', color: COLORS.subtext, fontSize: '0.9375rem' }}>
              This link is no longer valid. It may have already been used, revoked, or expired. Please
              contact Backed by Humans if you believe this is a mistake.
            </p>
          </>
        )}

        {state === 'submitted' && (
          <>
            <h1 style={{ margin: 0, fontSize: '1.25rem', color: COLORS.text }}>Thank you — signed</h1>
            <p style={{ marginTop: '0.75rem', color: COLORS.subtext, fontSize: '0.9375rem' }}>
              Your signature has been recorded. A copy of the signed document will be emailed to you
              shortly.
            </p>
          </>
        )}

        {state === 'ready' && data && (
          <form onSubmit={handleSubmit} noValidate>
            <h1 style={{ margin: 0, fontSize: '1.25rem', color: COLORS.text }}>{data.title}</h1>

            <div
              style={{
                marginTop: '1rem',
                marginBottom: '1.25rem',
                maxHeight: 320,
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                color: COLORS.text,
                background: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                padding: '0.875rem 1rem',
              }}
            >
              {data.body}
            </div>

            {data.fields.map((field) => (
              <div key={field.key} style={{ marginBottom: '0.875rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: COLORS.text,
                  }}
                >
                  {field.label}
                  {field.required ? ' *' : ''}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={values[field.key] || ''}
                    onChange={(e) => updateValue(field.key, e.target.value)}
                    required={field.required}
                    rows={3}
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.inputBorder}`,
                      borderRadius: 8,
                      padding: '0.625rem 0.75rem',
                      fontSize: '0.9375rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={values[field.key] || ''}
                    onChange={(e) => updateValue(field.key, e.target.value)}
                    required={field.required}
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.inputBorder}`,
                      borderRadius: 8,
                      padding: '0.625rem 0.75rem',
                      fontSize: '0.9375rem',
                    }}
                  />
                )}
              </div>
            ))}

            <div style={{ marginTop: '1.25rem', marginBottom: '0.875rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.875rem', color: COLORS.text }}>
                Signature *
              </label>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.625rem' }}>
                <button
                  type="button"
                  onClick={() => setSignatureMode('typed')}
                  style={{
                    border: `1px solid ${signatureMode === 'typed' ? COLORS.accent : COLORS.inputBorder}`,
                    background: signatureMode === 'typed' ? '#f0fdfa' : '#fff',
                    color: signatureMode === 'typed' ? COLORS.accent : COLORS.subtext,
                    borderRadius: 8,
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Type
                </button>
                <button
                  type="button"
                  onClick={() => setSignatureMode('drawn')}
                  style={{
                    border: `1px solid ${signatureMode === 'drawn' ? COLORS.accent : COLORS.inputBorder}`,
                    background: signatureMode === 'drawn' ? '#f0fdfa' : '#fff',
                    color: signatureMode === 'drawn' ? COLORS.accent : COLORS.subtext,
                    borderRadius: 8,
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Draw
                </button>
              </div>

              {signatureMode === 'typed' ? (
                <>
                  <input
                    type="text"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                    placeholder="Type your full name"
                    style={{
                      width: '100%',
                      border: `1px solid ${COLORS.inputBorder}`,
                      borderRadius: 8,
                      padding: '0.625rem 0.75rem',
                      fontSize: '1.5rem',
                      fontFamily: "'Brush Script MT', cursive",
                    }}
                  />
                </>
              ) : (
                <div>
                  <canvas
                    ref={canvasRef}
                    width={480}
                    height={140}
                    style={{
                      width: '100%',
                      maxWidth: 480,
                      height: 140,
                      border: `1px solid ${COLORS.inputBorder}`,
                      borderRadius: 8,
                      touchAction: 'none',
                      cursor: 'crosshair',
                    }}
                    onMouseDown={(e) => startDraw(e.clientX, e.clientY)}
                    onMouseMove={(e) => drawTo(e.clientX, e.clientY)}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={(e) => {
                      const t = e.touches[0];
                      if (t) startDraw(t.clientX, t.clientY);
                    }}
                    onTouchMove={(e) => {
                      const t = e.touches[0];
                      if (t) drawTo(t.clientX, t.clientY);
                    }}
                    onTouchEnd={endDraw}
                  />
                  <button
                    type="button"
                    onClick={clearCanvas}
                    style={{
                      display: 'block',
                      marginTop: '0.5rem',
                      border: 'none',
                      background: 'transparent',
                      color: COLORS.subtext,
                      fontSize: '0.8125rem',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {error && (
              <p style={{ marginTop: '0.5rem', marginBottom: '0.75rem', color: COLORS.error, fontSize: '0.875rem' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: 8,
                padding: '0.625rem 1rem',
                fontWeight: 600,
                color: '#fff',
                background: submitting ? COLORS.accentDisabled : COLORS.accent,
                cursor: submitting ? 'not-allowed' : 'pointer',
                marginTop: '0.5rem',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit signature'}
            </button>

            <p style={{ marginTop: '0.75rem', color: COLORS.subtext, fontSize: '0.8125rem' }}>
              If you have any questions please email{' '}
              <a href="mailto:hello@backedbyhumans.com" style={{ color: COLORS.accent }}>
                hello@backedbyhumans.com
              </a>
              .
            </p>
          </form>
        )}
      </section>
    </main>
  );
};

export default Sign;
